import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import { supabase } from "../supabaseClient";
import { sanityClient } from "../utils/sanityClient";

const CATEGORY_GRADIENTS = {
  Announcement: "bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] text-black",
  Update: "bg-[linear-gradient(135deg,#ffb56f,#c85f47)]",
  Event: "bg-[linear-gradient(135deg,#ff8f6b,#8f3f32)]",
  "Patch Notes": "bg-[linear-gradient(135deg,#009dff,#1238ff)]",
  News: "bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] text-black",
};

const DEFAULT_TABS = {
  hero: true,
  stats: true,
  news: true,
  play: true,
  modes: true,
};

export default function Home() {
  const [stats, setStats] = useState({
    favorites: 0,
    visits: 0,
    likes: 0,
  });

  const [newsPosts, setNewsPosts] = useState([]);
  const [tabs, setTabs] = useState(DEFAULT_TABS);

  const isTabEnabled = useMemo(() => {
    return (key) => tabs[key] !== false;
  }, [tabs]);

  useEffect(() => {
    loadTabs();
    loadStats();
    loadNews();

    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  async function loadTabs() {
    try {
      const { data, error } = await supabase
        .from("Tabs")
        .select("tab_key, enabled");

      if (error) throw error;

      const nextTabs = { ...DEFAULT_TABS };

      if (Array.isArray(data)) {
        data.forEach((tab) => {
          if (tab?.tab_key) {
            nextTabs[tab.tab_key] = tab.enabled === true;
          }
        });
      }

      setTabs(nextTabs);
    } catch (err) {
      console.error("Failed to load homepage tabs:", err);
      setTabs(DEFAULT_TABS);
    }
  }

  async function loadNews() {
    try {
      const query = `*[_type == "newsPost"] | order(publishedAt desc)[0...6] {
        _id,
        title,
        "slug": slug.current,
        overview,
        category,
        publishedAt,
        "bannerImage": bannerImage.asset->url,
        "bannerAlt": bannerImage.alt
      }`;

      const data = await sanityClient.fetch(query);
      setNewsPosts(data || []);
    } catch (err) {
      console.error("Failed to load Sanity homepage news:", err);
      setNewsPosts([]);
    }
  }

  async function loadStats() {
    try {
      const res = await fetch("/api/roblox-stats");
      const data = await res.json();

      setStats({
        favorites: data.favorites || 0,
        visits: data.visits || 0,
        likes: data.likes || 0,
      });
    } catch (err) {
      console.error("Failed to load Roblox stats:", err);
    }
  }

  function formatNumber(value) {
    return new Intl.NumberFormat("en-GB").format(value || 0);
  }

  return (
    <main className="min-h-screen overflow-hidden bg-[#050505] text-white">
      <Header />

      {isTabEnabled("hero") && (
        <section className="relative flex min-h-screen items-center px-6 pt-32 md:px-[7%]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,217,120,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(255,143,107,0.24),transparent_38%)]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,#050505_0%,transparent_45%)]" />

          <div className="relative z-10 max-w-4xl">
            <h1 className="text-[clamp(72px,12vw,160px)] leading-[0.82] text-[#ffd978] drop-shadow-[0_0_22px_#ff8f6b]">
              DROP IN.
              <br />
              SURVIVE THE
              <br />
              OUTFALL.
            </h1>

            <p className="mt-7 max-w-2xl text-xl font-bold leading-relaxed text-white md:text-2xl">
              A fast-paced battle royale where every rotation, fight, and
              decision matters.
            </p>

            {isTabEnabled("stats") && (
              <div className="mt-7 grid max-w-2xl grid-cols-3 gap-3">
                <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/45 p-4 text-center backdrop-blur-md">
                  <p className="text-3xl font-black text-[#ffd978]">
                    {formatNumber(stats.favorites)}
                  </p>
                  <p className="mt-1 text-sm font-black uppercase text-white">
                    Favorites
                  </p>
                </div>

                <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/45 p-4 text-center backdrop-blur-md">
                  <p className="text-3xl font-black text-[#ffd978]">
                    {formatNumber(stats.visits)}
                  </p>
                  <p className="mt-1 text-sm font-black uppercase text-white">
                    Visits
                  </p>
                </div>

                <div className="rounded-2xl border border-[#ffb56f]/30 bg-black/45 p-4 text-center backdrop-blur-md">
                  <p className="text-3xl font-black text-[#ffd978]">
                    {formatNumber(stats.likes)}
                  </p>
                  <p className="mt-1 text-sm font-black uppercase text-white">
                    Likes
                  </p>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-4">
              {isTabEnabled("play") && (
                <a href="#play" className="outfall-button">
                  Play Now
                </a>
              )}

              {isTabEnabled("news") && (
                <a
                  href="#news"
                  className="outfall-button outfall-button-secondary"
                >
                  Latest News
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {isTabEnabled("news") && (
        <section id="news" className="px-6 py-24 md:px-[7%]">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-black uppercase text-[#ffb56f]">
                Latest Updates
              </p>

              <h2 className="outfall-heading text-[clamp(54px,8vw,100px)] leading-none text-[#ffd978] drop-shadow-[0_0_16px_#ff8f6b]">
                What’s New
              </h2>
            </div>

            <a href="/news" className="outfall-button outfall-button-secondary">
              View All News
            </a>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {newsPosts.length === 0 ? (
              <p className="col-span-3 font-bold text-white/30">
                No posts yet — check back soon.
              </p>
            ) : (
              newsPosts.map((post) => {
                const gradient =
                  CATEGORY_GRADIENTS[post.category] ||
                  CATEGORY_GRADIENTS.Announcement;

                const isDark =
                  post.category === "Announcement" || post.category === "News";

                return (
                  <article
                    key={post._id}
                    className={`outfall-card scroll-shine relative flex min-h-[280px] flex-col justify-between overflow-hidden p-8 ${gradient}`}
                  >
                    {post.bannerImage && (
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-20"
                        style={{
                          backgroundImage: `url(${post.bannerImage})`,
                        }}
                      />
                    )}

                    <div className="relative z-10">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-black uppercase ${
                          isDark
                            ? "bg-black/20 text-black"
                            : "bg-white/20 text-white"
                        }`}
                      >
                        {post.category || "News"}
                      </span>

                      <a href={`/news/${post.slug}`}>
                        <h3
                          className={`mt-4 text-4xl font-black leading-tight transition hover:underline ${
                            isDark ? "text-black" : "text-[#ffd978]"
                          }`}
                        >
                          {post.title}
                        </h3>
                      </a>

                      <p
                        className={`mt-3 line-clamp-4 text-base font-bold ${
                          isDark ? "text-black/80" : "text-white"
                        }`}
                      >
                        {post.overview}
                      </p>
                    </div>

                    <p
                      className={`relative z-10 mt-5 text-xs font-bold ${
                        isDark ? "text-black/50" : "text-white/50"
                      }`}
                    >
                      By Outfall Staff &middot;{" "}
                      {post.publishedAt
                        ? new Date(post.publishedAt).toLocaleDateString(
                            "en-GB",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )
                        : ""}
                    </p>
                  </article>
                );
              })
            )}
          </div>
        </section>
      )}

      {isTabEnabled("play") && (
        <section
          id="play"
          className="scroll-shine mx-6 mb-20 rounded-[28px] border-4 border-[#ffd978] bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] px-6 py-24 text-center text-black shadow-[0_0_35px_rgba(255,143,107,0.45)] md:mx-[7%]"
        >
          <h2 className="text-[clamp(54px,8vw,105px)] leading-none">
            Ready To Drop?
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-2xl font-black">
            Play Outfall now and become the last one standing.
          </p>

          <a href="" className="outfall-button mt-8">
            Install Launcher
          </a>
        </section>
      )}

      {isTabEnabled("modes") && (
        <section
          id="modes"
          className="grid gap-6 px-6 pb-24 md:px-[7%] lg:grid-cols-2"
        >
          <article className="scroll-shine outfall-card min-h-[180px] bg-[linear-gradient(135deg,#ffd978,#ff8f6b)] p-8 text-black">
            <h3 className="text-center text-6xl">Solo</h3>

            <p className="mt-4 text-center text-lg font-black">
              Drop in alone, trust nobody, and fight for the Victory.
            </p>
          </article>

          <article className="scroll-shine outfall-card min-h-[180px] bg-[linear-gradient(135deg,#ffb56f,#8f3f32)] p-8">
            <h3 className="text-center text-6xl text-[#ffd978]">Team Royale</h3>

            <p className="mt-4 text-center text-lg font-black text-white">
              Squad up, control the battlefield, and survive the Outfall
              together.
            </p>
          </article>
        </section>
      )}
    </main>
  );
}
